import io from 'socket.io-client';
import { VotingOption } from '../types';

const isRealtimeTraceEnabled = (): boolean => localStorage.getItem('traceRealtime') === '1';

const traceRealtime = (event: string, payload?: Record<string, unknown>): void => {
  if (!isRealtimeTraceEnabled()) return;
  console.log(`[TRACE_RT][${new Date().toISOString()}] ${event}`, payload || {});
};

// Función utilitaria para obtener el token
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('accessToken');
  traceRealtime('auth.token.presence', { hasToken: !!token });
  
  if (token) {
    traceRealtime('auth.token.shape', {
      tokenLength: token.length,
      parts: token.split('.').length,
    });
    
    try {
      // Verificar que el token tenga el formato JWT correcto (3 partes separadas por .)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('❌ [socketService] Token no tiene formato JWT válido (debe tener 3 partes):', parts.length);
        return null;
      }
      
      // Decodificar el token para verificar su expiración
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      const exp = payload.exp;
      traceRealtime('auth.token.validity', {
        exp,
        now,
        isValid: exp > now,
      });
      
      if (exp <= now) {
        console.warn('⚠️ [socketService] Token expirado, removiendo de localStorage');
        localStorage.removeItem('accessToken');
        return null;
      }
    } catch (error) {
      console.error('❌ [socketService] Error decodificando token:', error);
      return null;
    }
  }
  
  return token;
};

// Tipos para los eventos del socket
export interface SocketConnection {
  socket: any;
  isConnected: boolean;
  currentAssembly: string | null;
}

export interface AssemblyJoinData {
  assemblyId: string;
}

export interface ChatMessage {
  id: string;
  assemblyId: string;
  userId: string;
  userName: string;
  userRole: string;
  message: string;
  type: 'text' | 'system';
  timestamp: Date;
  avatar?: string;
}

export interface NotificationData {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'voting' | 'assembly' | 'quorum';
  priority: 'low' | 'medium' | 'high' | 'critical';
  data?: any;
  actionUrl?: string;
  actionText?: string;
  autoClose?: boolean;
  duration?: number;
  timestamp: Date;
  read: boolean;
}

export interface QuorumStatus {
  assemblyId: string;
  assemblyTitle: string;
  quorumRequired: number;
  quorumAchieved: number;
  totalCoefficient: number;
  attendeeCoefficient: number;
  isQuorumMet: boolean;
  totalUsers: number;
  presentUsers: number;
  representedApartmentsCount?: number;
  registeredUsers?: number;
  missingForQuorum?: number;
}

export interface VotingSessionData {
  id: string;
  question: string;
  description?: string;
  options: VotingOption[];
  type: string;
  status: string;
  duration: number;
  isAnonymous: boolean;
  allowAbstention: boolean;
  startedAt?: Date;
  endedAt?: Date;
  endsAt?: Date;
  createdAt: Date;
}

export interface VotingResults {
  votingSessionId: string;
  status: string;
  totalEligibleVoters: number;
  totalVotes: number;
  totalCoefficient: number;
  votedCoefficient: number;
  participationRate: number;
  quorumMet: boolean;
  results: Array<{
    option: string;
    votes: number;
    coefficient: number;
    percentage: number;
    percentageByCoefficient: number;
  }>;
  winner?: string;
  isTie?: boolean;
  voterDetails?: Array<{
    userId: string;
    userName: string;
    apartment: string;
    vote: string;
    coefficient: number;
    votedAt: Date;
  }>;
}

export interface PresenceUser {
  id: string;
  name: string;
  email: string;
  role: string;
  isPresent: boolean;
  connectedAt?: Date;
  lastActivity?: Date;
}

class SocketService {
  private socket: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 2000;
  private currentAssembly: string | null = null;
  private joinedAssembly: string | null = null;
  private pendingAssembly: string | null = null;
  private eventListeners: Map<string, Function[]> = new Map();
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isReconnecting = false;
  private isManualDisconnect = false;

  constructor() {
    // No inicializar socket automáticamente - esperamos hasta que el usuario esté autenticado
    console.log('🏗️ [socketService] SocketService creado, esperando autenticación para conectar...');
  }

  private async initializeSocket(): Promise<void> {
    try {
      const token = getAuthToken();
      if (!token) {
        traceRealtime('socket.connect.skipped.no-token');
        console.log('⏭️ [socketService] Conexión Socket.IO omitida: no hay token válido');
        return;
      }
      const serverUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
      
      console.log('🔌 [socketService] Conectando a Socket.IO:', serverUrl);
      traceRealtime('socket.connect.init', { hasToken: !!token, serverUrl });
      
      this.socket = io(serverUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: false,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 10000, // Máximo 10 segundos entre intentos
        autoConnect: true, // Conectar automáticamente
        upgrade: true, // Permitir upgrade de polling a websocket
        rememberUpgrade: true // Recordar el upgrade exitoso
      });

      this.setupSocketListeners();
      this.startHeartbeat(); // Iniciar keepalive activo
      
    } catch (error) {
      console.error('❌ [socketService] Error inicializando socket:', error);
    }
  }

  // Heartbeat para mantener la conexión viva
  private startHeartbeat(): void {
    // Limpiar heartbeat anterior si existe
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Enviar ping cada 20 segundos
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        console.log('💓 [socketService] Enviando heartbeat...');
        // 🔑 NUEVA FUNCIONALIDAD: Incluir assemblyId en el ping para que el servidor sepa si está en asamblea
        this.socket.emit('ping', { 
          timestamp: Date.now(),
          assemblyId: this.currentAssembly || undefined
        });
      } else {
        console.warn('⚠️ [socketService] Socket desconectado durante heartbeat');
        // Intentar reconectar si no está conectado
        if (!this.isReconnecting && this.socket) {
          this.handleDisconnection();
        }
      }
    }, 20000); // Cada 20 segundos
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private handleDisconnection(): void {
    if (this.isReconnecting) return;
    
    this.isReconnecting = true;
    console.log('🔄 [socketService] Intentando reconexión...');

    // Preservar la asamblea actual para un único rejoin en el evento connect
    if (this.currentAssembly && !this.pendingAssembly) {
      this.pendingAssembly = this.currentAssembly;
    }
    
    // Desconectar y reconectar
    setTimeout(() => {
      if (this.socket) {
        this.socket.connect();
      }
      this.isReconnecting = false;
    }, 2000);
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Eventos de conexión
    this.socket.on('connect', () => {
      console.log('✅ Conectado al servidor Socket.IO');
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      this.emit('socket:connected', { isConnected: true });
      traceRealtime('socket.connected', {
        socketId: this.socket?.id,
        currentAssembly: this.currentAssembly,
        pendingAssembly: this.pendingAssembly,
      });
      
      if (this.pendingAssembly) {
        const assemblyToRejoin = this.pendingAssembly;
        console.log('🔄 Reconectado, re-uniéndose a asamblea pendiente:', assemblyToRejoin);
        this.pendingAssembly = null;
        this.joinAssembly(assemblyToRejoin).catch((error) => {
          console.error('❌ Error re-uniéndose a asamblea tras reconexión:', error);
        });
      } else if (this.currentAssembly) {
        console.log('🔄 Reconectado, re-uniéndose a currentAssembly:', this.currentAssembly);
        this.joinAssembly(this.currentAssembly).catch((error) => {
          console.error('❌ Error re-uniéndose a asamblea tras reconexión:', error);
        });
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('❌ Desconectado del servidor Socket.IO:', reason);
      traceRealtime('socket.disconnected', {
        reason,
        socketId: this.socket?.id,
        currentAssembly: this.currentAssembly,
        joinedAssembly: this.joinedAssembly,
      });
      if (!this.isManualDisconnect) {
        this.joinedAssembly = null;
      }
      this.emit('socket:disconnected', { isConnected: false, reason });
      
      if (reason !== 'io client disconnect' && reason !== 'io server disconnect') {
        console.log('🔄 Desconexión inesperada, intentando reconectar...');
        this.handleDisconnection();
      }
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('❌ Error de conexión Socket.IO:', error);
      this.handleConnectionError(error);
    });

    // Respuesta al heartbeat
    this.socket.on('pong', (data: { timestamp: number }) => {
      const latency = Date.now() - data.timestamp;
      console.log(`💓 [socketService] Pong recibido, latencia: ${latency}ms`);
    });

    // 🔑 NUEVA FUNCIONALIDAD: Escuchar evento de renovación de token del servidor
    this.socket.on('token:refresh', async (data: { timestamp: number, assemblyId?: string }) => {
      console.log('🔄 [socketService] Solicitud de renovación de token recibida');
      
      try {
        // Llamar al endpoint para renovar el token
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({
            assemblyId: data.assemblyId || this.currentAssembly
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.tokens) {
            // Actualizar tokens en localStorage
            localStorage.setItem('accessToken', result.tokens.accessToken);
            localStorage.setItem('refreshToken', result.tokens.refreshToken);
            console.log('✅ [socketService] Token renovado exitosamente');
            this.emit('token:refreshed', result.tokens);
          }
        } else {
          console.error('❌ [socketService] Error renovando token:', response.status);
        }
      } catch (error) {
        console.error('❌ [socketService] Error en renovación de token:', error);
      }
    });

    // 🔐 NUEVA FUNCIONALIDAD: Escuchar evento de sesión reemplazada (login desde otro dispositivo)
    this.socket.on('session:replaced', (data: { message: string, newSessionId: string, timestamp: number }) => {
      console.log('⚠️ [socketService] Sesión reemplazada desde otro dispositivo:', data);
      
      // Limpiar tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      // Emitir evento para que la app maneje el logout con el modal
      this.emit('session:replaced', data);
      
      // Desconectar socket
      this.disconnect();
    });

    // Eventos de autenticación
    this.socket.on('auth:success', (data: any) => {
      console.log('✅ Autenticación Socket.IO exitosa:', data);
      this.emit('auth:success', data);
    });

    this.socket.on('auth:error', (error: any) => {
      console.error('❌ Error de autenticación Socket.IO:', error);
      this.emit('auth:error', error);
      this.disconnect();
    });

    // Eventos de asamblea
    this.socket.on('joined:assembly', (data: any) => {
      console.log('✅ Unido a asamblea:', data);
      this.currentAssembly = data.assemblyId;
      this.joinedAssembly = data.assemblyId;
      traceRealtime('assembly.joined', {
        assemblyId: data.assemblyId,
        participants: data.participants,
      });
      this.emit('assembly:joined', data);
    });

    this.socket.on('left:assembly', (data: any) => {
      console.log('👋 Salió de asamblea:', data);
      this.currentAssembly = null;
      this.joinedAssembly = null;
      this.emit('assembly:left', data);
    });

    // Eventos de ciclo de vida de asambleas (para auto-join/leave)
    this.socket.on('assembly:started', (data: { assembly?: any }) => {
      console.log('🚀 [socketService] assembly:started recibido del servidor:', data);
      this.emit('assembly:started', data);
    });

    this.socket.on('assembly:completed', (data: { assembly?: any }) => {
      console.log('✅ [socketService] assembly:completed recibido del servidor:', data);
      this.emit('assembly:completed', data);
    });

    // Eventos de participantes
    this.socket.on('user:joined', (data: { userId: string; userName: string; participants: number }) => {
      console.log('👤 Usuario se unió:', data);
      this.emit('user:joined', data);
    });

    this.socket.on('user:rejoined', (data: { userId: string; userName: string; participants: number }) => {
      console.log('🔄 Usuario se re-unió:', data);
      this.emit('user:rejoined', data);
    });

    this.socket.on('user:left', (data: { userId: string; userName: string; participants: number }) => {
      console.log('👋 Usuario salió:', data);
      this.emit('user:left', data);
    });

    // Eventos de quórum
    this.socket.on('quorum:status-updated', (data: { quorum: QuorumStatus }) => {
      this.emit('quorum:updated', data.quorum);
    });

    this.socket.on('quorum:achieved', (data: any) => {
      this.emit('quorum:achieved', data);
    });

    this.socket.on('quorum:lost', (data: any) => {
      this.emit('quorum:lost', data);
    });

    // Evento de cambio en participantes elegibles (por delegaciones)
    this.socket.on('eligible:participants-changed', (data: any) => {
      console.log('👥 [socketService] eligible:participants-changed recibido:', data);
      this.emit('eligible:participants-changed', data);
      // Disparar evento en window para que lo escuchen los componentes
      window.dispatchEvent(new CustomEvent('eligible:participants-changed', { detail: data }));
    });

    // Eventos de votación
    this.socket.on('voting:session-created', (data: { votingSession: VotingSessionData }) => {
      console.log('📥 [Socket] voting:session-created recibido:', data);
      this.emit('voting:session-created', data.votingSession);
    });

    this.socket.on('voting:session-started', (data: { votingSession: VotingSessionData }) => {
      console.log('📥 [Socket] voting:session-started recibido:', data);
      this.emit('voting:session-started', data.votingSession);
    });

    this.socket.on('voting:session-updated', (data: any) => {
      console.log('📥 [Socket] voting:session-updated recibido:', data);
      this.emit('voting:session-updated', data);
    });

    this.socket.on('voting:session-ended', (data: { votingSession: VotingSessionData; results: VotingResults }) => {
      console.log('📥 [Socket] voting:session-ended recibido:', data);
      // Emitimos con nombre de propiedad consistente para el consumidor (votingSession)
      this.emit('voting:session-ended', { votingSession: data.votingSession, results: data.results });
    });

    this.socket.on('voting:session-deleted', (data: { id: string; reason?: string }) => {
      console.log('📥 [Socket] voting:session-deleted recibido:', data);
      this.emit('voting:session-deleted', data);
    });

    this.socket.on('voting:vote-count-updated', (data: { votingSessionId: string; totalVotes: number }) => {
      console.log('📥 [Socket] voting:vote-count-updated recibido:', data);
      this.emit('voting:vote-count-updated', data);
    });

    this.socket.on('voting:live-results', (data: { results: VotingResults }) => {
      this.emit('voting:live-results', data.results);
    });

    this.socket.on('voting:vote-cast', (data: any) => {
      console.log('📥 [Socket] voting:vote-cast recibido:', data);
      this.emit('voting:vote-cast', data);
    });

    // Eventos de chat
    this.socket.on('chat:message', (message: ChatMessage) => {
      traceRealtime('chat.message.received', {
        messageId: message.id,
        assemblyId: message.assemblyId,
        userId: message.userId,
      });
      this.emit('chat:message', message);
    });

    this.socket.on('chat:typing', (data: { userId: string; userName: string; isTyping: boolean; assemblyId?: string }) => {
      this.emit('chat:typing', data);
    });

    // Eventos de notificaciones
    this.socket.on('notification:new', (notification: NotificationData) => {
      this.emit('notification:new', notification);
    });

    this.socket.on('notification:assembly', (notification: NotificationData) => {
      this.emit('notification:assembly', notification);
    });

    this.socket.on('notification:system', (notification: NotificationData) => {
      this.emit('notification:system', notification);
    });

    // Eventos de presencia
    this.socket.on('presence:updated', (data: { userId: string; userName: string; status: string }) => {
      this.emit('presence:updated', data);
    });

    // Eventos de importación masiva
    this.socket.on('import:progress', (data: any) => {
      this.emit('import:progress', data);
    });

    this.socket.on('import:completed', (data: any) => {
      this.emit('import:completed', data);
    });

    // Eventos de sistema
    this.socket.on('force:disconnect', (data: { reason: string }) => {
      console.warn('🚨 Desconexión forzada:', data.reason);
      this.emit('force:disconnect', data);
      this.disconnect();
    });

    this.socket.on('error', (error: any) => {
      console.error('❌ Error del socket:', error);
      this.emit('socket:error', error);
    });
  }

  private handleConnectionError(error: any): void {
    this.reconnectAttempts++;
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Máximo número de intentos de reconexión alcanzado');
      this.emit('socket:max-reconnect-attempts', { attempts: this.reconnectAttempts });
      return;
    }

    console.log(`🔄 Reintentando conexión (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    setTimeout(() => {
      this.reconnect();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  // Métodos públicos para gestión de conexión
  public async connect(): Promise<void> {
    console.log('🔌 [socketService] connect() llamado');
    console.log('🔍 [socketService] Estado actual - socket:', !!this.socket, 'connected:', this.socket?.connected);

    const token = getAuthToken();
    if (!token) {
      traceRealtime('socket.connect.aborted.no-token');
      console.log('⏭️ [socketService] connect() abortado: usuario sin sesión válida');
      return;
    }
    
    if (!this.socket || this.socket.disconnected) {
      console.log('🚀 [socketService] Iniciando nueva conexión...');
      await this.initializeSocket();
    } else if (this.socket.connected) {
      console.log('✅ [socketService] Socket ya está conectado');
    }
  }

  private async waitForConnection(timeoutMs: number = 5000): Promise<boolean> {
    if (this.socket?.connected) return true;
    if (!this.socket) return false;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        cleanup();
        resolve(false);
      }, timeoutMs);

      const onConnect = () => {
        cleanup();
        resolve(true);
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.socket?.off('connect', onConnect);
      };

      this.socket.on('connect', onConnect);
    });
  }

  public disconnect(): void {
    this.isManualDisconnect = true;
    this.pendingAssembly = null;
    if (this.socket) {
      console.log('🔌 Desconectando socket...');
      this.stopHeartbeat();
      this.socket.disconnect();
      this.socket = null;
      this.currentAssembly = null;
      this.joinedAssembly = null;
      this.isReconnecting = false;
    }
  }

  public reconnect(): void {
    const token = getAuthToken();
    if (!token) {
      traceRealtime('socket.reconnect.aborted.no-token');
      console.log('⏭️ [socketService] reconnect() abortado: no hay token válido');
      return;
    }

    if (this.currentAssembly && !this.isManualDisconnect) {
      this.pendingAssembly = this.currentAssembly;
      console.log('🔍 [socketService] Preservando pendingAssembly:', this.pendingAssembly);
    }
    
    this.isManualDisconnect = false;
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.initializeSocket();
  }

  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  public getCurrentAssembly(): string | null {
    return this.currentAssembly;
  }

  private async waitForAssemblyJoin(assemblyId: string, timeoutMs: number = 4000): Promise<boolean> {
    if (this.joinedAssembly === assemblyId) {
      return true;
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.off('assembly:joined', handleJoined);
        resolve(false);
      }, timeoutMs);

      const handleJoined = (data: { assemblyId?: string }) => {
        if (data?.assemblyId !== assemblyId) {
          return;
        }
        clearTimeout(timeout);
        this.off('assembly:joined', handleJoined);
        resolve(true);
      };

      this.on('assembly:joined', handleJoined);
    });
  }

  // Métodos para unirse/salir de asambleas
  public async joinAssembly(assemblyId: string, maxRetries: number = 3): Promise<void> {
    this.currentAssembly = assemblyId;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (!this.socket || !this.socket.connected) {
        await this.connect();
        const connected = await this.waitForConnection(5000);

        if (!connected) {
          console.warn(`⚠️ Intento ${attempt}/${maxRetries}: Socket no conectado, reintentando...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
      }

      if (this.socket?.connected) {
        console.log('🏛️ Uniéndose a asamblea:', assemblyId);
        traceRealtime('assembly.join.request', {
          attempt,
          assemblyId,
          socketId: this.socket?.id,
        });
        this.socket.emit('join:assembly', { assemblyId });

        const joined = await this.waitForAssemblyJoin(assemblyId, 5000);
        if (joined) {
          traceRealtime('assembly.join.confirmed', { assemblyId, attempt });
          return;
        }

        console.warn(`⚠️ Intento ${attempt}/${maxRetries}: No llegó confirmación joined:assembly, reintentando...`);
      }
    }

    this.joinedAssembly = null;
    console.warn('⚠️ No se pudo confirmar unión a la asamblea después de varios intentos.');
    this.emit('socket:join-failed', { assemblyId });
    throw new Error('No se pudo confirmar unión a la asamblea');
  }

  public async leaveAssembly(assemblyId: string): Promise<void> {
    if (!this.socket?.connected) {
      this.currentAssembly = null;
      this.joinedAssembly = null;
      return;
    }

    console.log('👋 Saliendo de asamblea:', assemblyId);
    this.socket.emit('leave:assembly', { assemblyId });
    this.currentAssembly = null;
    this.joinedAssembly = null;
  }

  // Métodos para votación
  public subscribeToVotingResults(votingSessionId: string): void {
    if (!this.socket?.connected) return;
    
    this.socket.emit('voting:subscribe-results', { votingSessionId });
  }

  public unsubscribeFromVotingResults(votingSessionId: string): void {
    if (!this.socket?.connected) return;
    
    this.socket.emit('voting:unsubscribe-results', { votingSessionId });
  }

  public acknowledgeVote(votingSessionId: string, messageId: string): void {
    if (!this.socket?.connected) return;
    
    this.socket.emit('voting:ack', { votingSessionId, messageId });
  }

  // Métodos para chat
  public async sendChatMessage(
    assemblyId: string, 
    message: string, 
    type: string = 'text',
    maxRetries: number = 3
  ): Promise<void> {
    if (!this.socket?.connected) {
      throw new Error('Socket no está conectado');
    }

    if (!message.trim()) {
      if (this.joinedAssembly !== assemblyId) {
        throw new Error('No se pudo confirmar unión a la asamblea para enviar mensaje');
      }
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        traceRealtime('chat.send.attempt', {
          attempt,
          assemblyId,
          joinedAssembly: this.joinedAssembly,
          socketConnected: this.socket?.connected,
        });
        const ack = await new Promise<{ success: boolean; message?: string }>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error('Timeout enviando mensaje de chat'));
          }, 5000);

          this.socket.emit(
            'chat:message',
            {
              assemblyId,
              message: message.trim(),
              type
            },
            (response: { success: boolean; message?: string }) => {
              clearTimeout(timeoutId);
              resolve(response || { success: false, message: 'Sin respuesta del servidor' });
            }
          );
        });

        if (!ack.success) {
          throw new Error(ack.message || 'El servidor rechazó el mensaje');
        }

        traceRealtime('chat.send.ack', {
          attempt,
          assemblyId,
          success: ack.success,
        });
        
        return;
      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        traceRealtime('chat.send.error', {
          attempt,
          assemblyId,
          error: (error as Error).message,
          isLastAttempt,
        });
        console.warn(`⚠️ Intento ${attempt}/${maxRetries} falló:`, (error as Error).message);
        
        if (isLastAttempt) {
          throw error;
        }
        
        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  public sendTypingIndicator(assemblyId: string, isTyping: boolean): void {
    if (!this.socket?.connected) return;
    
    this.socket.emit('chat:typing', { assemblyId, isTyping });
  }

  // Métodos para presencia
  public updatePresence(assemblyId: string, status: 'present' | 'absent'): void {
    if (!this.socket?.connected) return;
    
    this.socket.emit('presence:update', { assemblyId, status });
  }

  // Métodos para notificaciones
  public markNotificationAsRead(notificationId: string): void {
    if (!this.socket?.connected) return;
    
    this.socket.emit('notification:read', { notificationId });
  }

  public requestPendingNotifications(): void {
    if (!this.socket?.connected) return;
    
    this.socket.emit('notifications:request');
  }

  // Sistema de eventos personalizado
  public on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)?.push(callback);
  }

  public off(event: string, callback?: Function): void {
    if (!this.eventListeners.has(event)) return;
    
    if (callback) {
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      this.eventListeners.set(event, []);
    }
  }

  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error en listener del evento ${event}:`, error);
        }
      });
    }
  }

  // Método para limpiar todos los listeners
  public removeAllListeners(): void {
    this.eventListeners.clear();
  }

  // ========== EVENTOS DE IMPORTACIÓN ==========
  
  /**
   * Escuchar actualizaciones de progreso de importación
   */
  public onImportProgress(callback: (data: any) => void): void {
    this.on('import:progress', callback);
  }

  /**
   * Escuchar evento de importación completada
   */
  public onImportCompleted(callback: (data: any) => void): void {
    this.on('import:completed', callback);
  }

  /**
   * Dejar de escuchar eventos de importación
   */
  public removeImportListeners(): void {
    this.off('import:progress');
    this.off('import:completed');
  }

  // Método para obtener información de debug
  public getDebugInfo(): any {
    return {
      isConnected: this.isConnected(),
      currentAssembly: this.currentAssembly,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.socket?.id,
      transport: this.socket?.io.engine.transport.name,
      listeners: Array.from(this.eventListeners.keys())
    };
  }
}

// Crear instancia singleton
export const socketService = new SocketService();

export default socketService;